// Seçim yokken 5 KPI ekranının ortak boş durumu (kural 19, mockup 13): "Bir müşteri seçin" +
// arama + müşteri listesi (set sayısı); seti olmayan satırda "Katalogdan Set Oluştur".
//
// ClientPicker işi (2026-10-07): liste artık ölçeklenir — boşken "Son kullanılanlar" (varsa)
// + ilk 12 müşteri + "N müşteri daha — aramayı daraltın"; yazınca 250 ms debounce ile sunucu
// araması (`/kpi/clients-summary?search=`, ≤30). Set adları satırda değil `title`'da
// (hover) — satır "4 set" der.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listKpiClientsSummary, type KpiClientSummary } from "../../api/kpi";
import {
  CLIENT_PICKER_COPY,
  fillCopy,
  pushRecentClient,
  readRecentClients,
} from "../../lib/client-picker";
import { initialsOf, KPI_COPY, kpiSetLabel, kpiSetsSummaryLabel } from "../../lib/kpi-ui";
import { useAuthStore } from "../../stores/auth-store";
import { useKpiStore } from "../../stores/kpi-store";
import { PICKER_DEBOUNCE_MS } from "../common/ClientPickerCore";

export const KPI_EMPTY_INITIAL_ROWS = 12;
export const KPI_EMPTY_SEARCH_LIMIT = 30;

export function KpiEmptyState({
  onCreateSet,
}: {
  onCreateSet?: (clientId: string) => void;
}): JSX.Element {
  const navigate = useNavigate();
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const { clients, clientsTotal, selectClient } = useKpiStore();
  const [q, setQ] = useState("");
  const [results, setResults] = useState<{
    query: string;
    rows: KpiClientSummary[];
    total: number;
  } | null>(null);
  const [searching, setSearching] = useState(false);
  const [recentRows, setRecentRows] = useState<KpiClientSummary[]>([]);

  // Son kullanılanlar: localStorage id listesi → özet satırı (ilk 50'de yoksa tekil çağrı).
  useEffect(() => {
    const recent = readRecentClients(userId);
    if (recent.length === 0) {
      setRecentRows([]);
      return;
    }
    const known = recent
      .map((r) => clients.find((c) => c.id === r.id))
      .filter((c): c is KpiClientSummary => !!c);
    const missing = recent.filter((r) => !clients.some((c) => c.id === r.id)).map((r) => r.id);
    if (missing.length === 0) {
      setRecentRows(known);
      return;
    }
    let cancelled = false;
    listKpiClientsSummary({ ids: missing })
      .then((res) => {
        if (cancelled) return;
        const byId = new Map<string, KpiClientSummary>();
        for (const c of [...known, ...res.clients]) byId.set(c.id, c);
        setRecentRows(recent.map((r) => byId.get(r.id)).filter((c): c is KpiClientSummary => !!c));
      })
      .catch(() => {
        if (!cancelled) setRecentRows(known);
      });
    return () => {
      cancelled = true;
    };
  }, [clients, userId]);

  // Sunucu araması (debounce + yarış koruması: yanıt sorgusu güncel sorguyla eşleşmeli).
  useEffect(() => {
    const needle = q.trim();
    if (!needle) {
      setResults(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    const t = window.setTimeout(() => {
      listKpiClientsSummary({ search: needle, limit: KPI_EMPTY_SEARCH_LIMIT })
        .then((res) => setResults({ query: needle, rows: res.clients, total: res.total }))
        .catch(() => setResults({ query: needle, rows: [], total: 0 }))
        .finally(() => setSearching(false));
    }, PICKER_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [q]);

  const view = useMemo(() => {
    const needle = q.trim();
    if (needle) {
      const rows = results?.query === needle ? results.rows : [];
      const total = results?.query === needle ? results.total : 0;
      return { recent: [] as KpiClientSummary[], rows, more: Math.max(0, total - rows.length) };
    }
    const recentIds = new Set(recentRows.map((c) => c.id));
    const rows = clients.filter((c) => !recentIds.has(c.id)).slice(0, KPI_EMPTY_INITIAL_ROWS);
    const shown = rows.length + recentRows.length;
    return { recent: recentRows, rows, more: Math.max(0, clientsTotal - shown) };
  }, [q, results, clients, clientsTotal, recentRows]);

  const pending = q.trim() !== "" && (searching || results?.query !== q.trim());
  const nothing = !pending && view.recent.length === 0 && view.rows.length === 0;

  function open(c: KpiClientSummary): void {
    pushRecentClient(userId, { id: c.id, name: c.name });
    void selectClient(c.id);
  }

  return (
    <div
      className="rounded-xl p-7"
      style={{
        border: "1px dashed var(--color-border)",
        background: "var(--color-bg)",
        maxWidth: 760,
      }}
    >
      <h2 className="text-xl mb-1" style={{ color: "var(--color-text)", fontWeight: 600 }}>
        {KPI_COPY.emptyTitle}
      </h2>
      <p className="text-sm mb-4" style={{ color: "var(--color-text-2)" }}>
        {KPI_COPY.emptyBody}
      </p>
      <input
        type="search"
        aria-label={KPI_COPY.emptySearch}
        placeholder={KPI_COPY.emptySearch}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        style={{
          width: "100%",
          padding: "9px 12px",
          borderRadius: 8,
          border: "1px solid var(--color-border)",
          background: "var(--color-surface)",
          fontSize: "0.9rem",
        }}
      />
      <ul
        className="mt-3 rounded-xl overflow-hidden"
        aria-busy={pending || undefined}
        style={{
          border: "1px solid var(--color-border)",
          background: "var(--color-surface)",
          listStyle: "none",
          margin: "12px 0 0",
          padding: 0,
        }}
      >
        {pending ? (
          <li className="px-4 py-3 text-sm" role="status" style={{ color: "var(--color-text-3)" }}>
            {CLIENT_PICKER_COPY.loading}
          </li>
        ) : null}
        {nothing ? (
          <li className="px-4 py-3 text-sm" style={{ color: "var(--color-text-3)" }}>
            {KPI_COPY.noClientsMatch}
          </li>
        ) : null}
        {view.recent.length > 0 ? <SectionHeader label={CLIENT_PICKER_COPY.recent} /> : null}
        {view.recent.map((c) => (
          <ClientRow
            key={`r-${c.id}`}
            c={c}
            onOpen={open}
            onCreateSet={onCreateSet}
            navigate={navigate}
          />
        ))}
        {view.recent.length > 0 && view.rows.length > 0 ? (
          <SectionHeader label={CLIENT_PICKER_COPY.results} />
        ) : null}
        {view.rows.map((c) => (
          <ClientRow key={c.id} c={c} onOpen={open} onCreateSet={onCreateSet} navigate={navigate} />
        ))}
        {view.more > 0 ? (
          <li className="px-4 py-2 text-xs" style={{ color: "var(--color-text-3)" }}>
            {fillCopy(CLIENT_PICKER_COPY.more, { n: view.more })}
          </li>
        ) : null}
      </ul>
      <div className="text-xs mt-3" style={{ color: "var(--color-text-3)" }}>
        {KPI_COPY.emptyHint}
      </div>
    </div>
  );
}

function SectionHeader({ label }: { label: string }): JSX.Element {
  return (
    <li
      className="px-4 pt-2 pb-1"
      style={{
        fontSize: "0.68rem",
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        color: "var(--color-text-3)",
        fontWeight: 600,
      }}
    >
      {label}
    </li>
  );
}

function ClientRow({
  c,
  onOpen,
  onCreateSet,
  navigate,
}: {
  c: KpiClientSummary;
  onOpen: (c: KpiClientSummary) => void;
  onCreateSet: ((clientId: string) => void) | undefined;
  navigate: ReturnType<typeof useNavigate>;
}): JSX.Element {
  const initials = initialsOf(c.name); // P3-32: "Akçansa (efr)" → "A(" değil "A"
  const hasSets = c.setCount > 0;
  const setNames = c.sets.map(kpiSetLabel).join(" · "); // TODO-KPI-29: çerçeve adı etikette
  return (
    <li
      className="flex items-center gap-3 px-4 py-3"
      style={{ borderBottom: "1px solid var(--color-border)" }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: "var(--color-primary)",
          color: "var(--color-surface)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          fontSize: "0.8rem",
          flexShrink: 0,
        }}
      >
        {initials}
      </span>
      <span className="min-w-0 flex-1">
        <b style={{ display: "block", fontSize: "0.9rem", color: "var(--color-text)" }}>{c.name}</b>
        <span
          className="text-xs"
          style={{ color: "var(--color-text-2)" }}
          title={hasSets ? setNames : undefined}
        >
          {hasSets ? kpiSetsSummaryLabel(c.setCount, c.sets) : KPI_COPY.emptyNoSets}
        </span>
      </span>
      {hasSets ? (
        <button
          type="button"
          onClick={() => onOpen(c)}
          className="px-3 py-1.5 rounded-lg text-sm font-semibold"
          style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
        >
          {KPI_COPY.emptyOpen}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => {
            if (onCreateSet) onCreateSet(c.id);
            else void navigate(`/clients/kpi/katalog?clientId=${c.id}`);
          }}
          className="px-3 py-1.5 rounded-lg text-sm"
          style={{ border: "1px solid var(--color-border)", color: "var(--color-text)" }}
        >
          {KPI_COPY.emptyCreateSet}
        </button>
      )}
    </li>
  );
}
